import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { instanceToken } from "../../../utils/constant";
import {
  EditFieldLabel,
  EditPageLayout,
} from "../../../components/edit/EditPageLayout";
import PolicyPdfUpload from "./PolicyPdfUpload";
import {
  DOCUMENT_TYPE_OPTIONS,
  isValidScope,
  LANGUAGE_OPTIONS,
  loadProductTypes,
  resolveDocumentType,
  SCOPES,
  uploadDocument,
  validatePdfFiles,
} from "./policyDocumentUtils";

const CUSTOM_DOC_TYPE = "__custom__";

export default function CreatePolicyDocument({ homeAlert }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scopeParam = searchParams.get("scope");
  const scope = isValidScope(scopeParam) ? scopeParam : SCOPES.general;

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [productTypes, setProductTypes] = useState([]);
  const [productType, setProductType] = useState("");
  const [loadingProductTypes, setLoadingProductTypes] = useState(false);

  const [policyNo, setPolicyNo] = useState("");
  const [customerId, setCustomerId] = useState("");

  const [documentTypePreset, setDocumentTypePreset] = useState(
    DOCUMENT_TYPE_OPTIONS[0]
  );
  const [customDocumentType, setCustomDocumentType] = useState("");
  const [language, setLanguage] = useState("en");
  const [pdfFiles, setPdfFiles] = useState([]);

  useEffect(() => {
    if (!isValidScope(scopeParam)) {
      navigate(`/policy-documents/create?scope=${SCOPES.general}`, {
        replace: true,
      });
    }
  }, [scopeParam, navigate]);

  const fetchProductTypes = useCallback(async () => {
    setLoadingProductTypes(true);
    try {
      const types = await loadProductTypes(instanceToken.token);
      setProductTypes(types);
      setProductType((current) =>
        current && types.includes(current) ? current : types[0] || ""
      );
      if (!types.length) {
        homeAlert("No product names found in GW catalog.", true);
      }
    } catch (error) {
      console.error(error);
      homeAlert("Failed to load product names.", true);
    } finally {
      setLoadingProductTypes(false);
    }
  }, [homeAlert]);

  useEffect(() => {
    if (scope === SCOPES.general) {
      fetchProductTypes();
    }
  }, [scope, fetchProductTypes]);

  const scopeTitle = scope === SCOPES.general ? "General" : "Customer";

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    setPdfFiles((current) => {
      const names = new Set(current.map((f) => f.name));
      const merged = [...current];
      selected.forEach((file) => {
        if (!names.has(file.name)) merged.push(file);
      });
      return merged;
    });
    setErrors((prev) => ({ ...prev, file: undefined }));
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    setPdfFiles((current) => current.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = {};
    const useCustom = documentTypePreset === CUSTOM_DOC_TYPE;
    const documentType = resolveDocumentType(
      documentTypePreset,
      customDocumentType,
      useCustom
    );

    if (!documentType) {
      err.documentType = "Document type is required.";
    }

    if (scope === SCOPES.general) {
      if (!productType) err.productType = "Product name is required.";
    } else {
      if (!policyNo.trim()) err.policyNo = "Policy number is required.";
      if (!customerId.trim()) err.customerId = "Customer ID is required.";
    }

    validatePdfFiles(pdfFiles, err);

    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }

    const formData = new FormData();
    formData.append("document_type", documentType);
    formData.append("language", language);
    pdfFiles.forEach((file) => formData.append("file", file));

    if (scope === SCOPES.general) {
      formData.append("product_type", productType);
    } else {
      formData.append("policy_no", policyNo.trim());
      formData.append("customerId", customerId.trim());
    }

    try {
      setLoading(true);
      const res = await uploadDocument(scope, instanceToken.token, formData);
      const count = res.data?.count || pdfFiles.length;
      homeAlert(
        count === 1
          ? "Policy document has been uploaded."
          : `${count} policy documents have been uploaded.`,
        false
      );
      navigate(`/policy-documents?scope=${scope}`);
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.error || "Failed to upload policy document.";
      homeAlert(message, true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <EditPageLayout
      title={`Upload ${scopeTitle} Policy Document`}
      subtitle="Upload one or more PDFs for the same product or policy and document type."
      breadcrumbs={[
        { label: "Policy Documents", path: `/policy-documents?scope=${scope}` },
        { label: "Upload" },
      ]}
      listPath={`/policy-documents?scope=${scope}`}
      loading={loading}
      submitLabel="Upload"
      onSubmit={handleSubmit}
    >
      {scope === SCOPES.general ? (
        <FormControl fullWidth>
          <div className="policy-docs-product-type-header">
            <EditFieldLabel required>Product Name</EditFieldLabel>
            <button
              type="button"
              className="edit-btn edit-btn-back"
              onClick={fetchProductTypes}
              disabled={loadingProductTypes}
            >
              {loadingProductTypes ? "Loading..." : "Reload"}
            </button>
          </div>
          <Select
            value={productType}
            onChange={(e) => setProductType(e.target.value)}
            displayEmpty
            disabled={loadingProductTypes}
            error={!!errors.productType}
          >
            <MenuItem value="" disabled>
              {loadingProductTypes
                ? "Loading product names..."
                : productTypes.length
                  ? "Select product name"
                  : "No product names available"}
            </MenuItem>
            {productTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Product names from GW (e.g. Smart Saving Plus 5/3). Not Flow or Line of Business.
          </FormHelperText>
          {errors.productType && (
            <FormHelperText error>{errors.productType}</FormHelperText>
          )}
        </FormControl>
      ) : (
        <div className="edit-form-row-2">
          <div>
            <EditFieldLabel required>Policy No</EditFieldLabel>
            <TextField
              fullWidth
              value={policyNo}
              onChange={(e) => setPolicyNo(e.target.value)}
              error={!!errors.policyNo}
              helperText={errors.policyNo}
            />
          </div>
          <div>
            <EditFieldLabel required>Customer ID</EditFieldLabel>
            <TextField
              fullWidth
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              error={!!errors.customerId}
              helperText={errors.customerId}
            />
          </div>
        </div>
      )}

      <FormControl fullWidth>
        <EditFieldLabel required>Document Type</EditFieldLabel>
        <Select
          value={documentTypePreset}
          onChange={(e) => setDocumentTypePreset(e.target.value)}
          error={!!errors.documentType}
        >
          {DOCUMENT_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
          <MenuItem value={CUSTOM_DOC_TYPE}>Custom...</MenuItem>
        </Select>
        {documentTypePreset === CUSTOM_DOC_TYPE && (
          <TextField
            fullWidth
            sx={{ mt: 1 }}
            placeholder="Enter custom document type"
            value={customDocumentType}
            onChange={(e) => setCustomDocumentType(e.target.value)}
            error={!!errors.documentType}
            helperText={errors.documentType}
          />
        )}
        {documentTypePreset !== CUSTOM_DOC_TYPE && errors.documentType && (
          <FormHelperText error>{errors.documentType}</FormHelperText>
        )}
      </FormControl>

      <FormControl fullWidth>
        <EditFieldLabel required>Language</EditFieldLabel>
        <Select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          {LANGUAGE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          Mobile app shows only documents matching the user&apos;s app language.
        </FormHelperText>
      </FormControl>

      <PolicyPdfUpload
        files={pdfFiles}
        multiple
        onChange={handleFileChange}
        onRemove={handleRemoveFile}
        error={errors.file}
      />
    </EditPageLayout>
  );
}
